import { useCallback, useEffect, useRef, useState } from "react";
import { buildShareUrl, createShareToken, hashShareToken } from "@/lib/shareToken";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { supabase } from "@/lib/supabase";

type ActiveShare = { id: string };
type FunctionClient = NonNullable<typeof supabase>;
const shareRows = (client: FunctionClient) =>
  (client as unknown as { from: (table: "share_links") => any }).from("share_links");
const shareRpc = (client: FunctionClient) => client as unknown as {
  rpc: (name: "create_share_link" | "deactivate_share_link", args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
};

export default function ShareControls({ recordingId }: { recordingId: string }) {
  const [activeShare, setActiveShare] = useState<ActiveShare | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedStatus, setHasLoadedStatus] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [showReplaceConfirmation, setShowReplaceConfirmation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const urlInput = useRef<HTMLInputElement>(null);
  const loadSequence = useRef(0);
  const loadPromiseRef = useRef<Promise<ActiveShare | null> | null>(null);

  const loadActiveShare = useCallback(async (): Promise<ActiveShare | null> => {
    if (loadPromiseRef.current) return loadPromiseRef.current;
    const requestId = ++loadSequence.current;
    if (!supabase) {
      const unavailable = new Error("Sharing isn’t available just yet. Please try again later.");
      if (loadSequence.current === requestId) {
        setError(unavailable.message);
        setIsLoading(false);
      }
      throw unavailable;
    }

    setIsLoading(true);
    setError(null);
    const pending = (async () => {
      const { data, error: queryError } = await shareRows(supabase)
        .select("id")
        .eq("recording_id", recordingId)
        .eq("is_active", true)
        .maybeSingle();
      if (queryError) throw queryError;
      const active = data ? { id: (data as { id: string }).id } : null;
      if (loadSequence.current === requestId) {
        setActiveShare(active);
        setHasLoadedStatus(true);
      }
      return active;
    })();
    loadPromiseRef.current = pending;
    try {
      return await pending;
    } catch (loadError) {
      if (loadSequence.current === requestId) {
        setError(friendlyErrorMessage(loadError, "share", "We couldn’t check sharing settings. Please try again."));
      }
      throw loadError;
    } finally {
      if (loadPromiseRef.current === pending) loadPromiseRef.current = null;
      if (loadSequence.current === requestId) setIsLoading(false);
    }
  }, [recordingId]);

  useEffect(() => {
    setActiveShare(null);
    setShareUrl(null);
    setHasLoadedStatus(false);
    setShowReplaceConfirmation(false);
    void loadActiveShare().catch(() => undefined);
    return () => {
      loadSequence.current += 1;
      loadPromiseRef.current = null;
    };
  }, [loadActiveShare]);

  const shareOrCopy = useCallback(async (url: string) => {
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "A voice story from Home, Twice", text: "Listen to this voice story", url });
        setMessage("Share link ready to send.");
        return;
      } catch (shareError) {
        if (shareError && typeof shareError === "object" && "name" in shareError && (shareError as { name?: string }).name === "AbortError") return;
        // If native share is unavailable or fails, keep the link and offer copy.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setMessage("Share link copied. Send it to your family.");
    } catch {
      urlInput.current?.focus();
      urlInput.current?.select();
      setMessage("Your link is ready. Select and copy it, then send it to your family.");
    }
  }, []);

  const createAndShare = useCallback(async () => {
    if (!supabase) {
      setError("Sharing isn’t available just yet. Please try again later.");
      return;
    }
    setIsWorking(true);
    setError(null);
    setMessage(null);
    setShowReplaceConfirmation(false);
    try {
      const token = createShareToken();
      const tokenHash = await hashShareToken(token);
      const { data, error: createError } = await shareRpc(supabase).rpc("create_share_link", {
        p_recording_id: recordingId,
        p_token_hash: tokenHash,
      });
      if (createError) throw createError;
      const row = (Array.isArray(data) ? data[0] : data) as { id?: string } | null;
      if (!row?.id) throw new Error("No share link was returned.");
      const url = buildShareUrl(token, window.location.origin);
      if (!url) throw new Error("Could not create a valid share link.");
      setActiveShare({ id: row.id });
      setShareUrl(url);
      setHasLoadedStatus(true);
      setMessage(null);
    } catch (createError) {
      setError(friendlyErrorMessage(createError, "share", "We couldn’t create the share link. Please try again."));
    } finally {
      setIsWorking(false);
    }
  }, [recordingId]);

  const requestCreateLink = useCallback(async () => {
    setIsWorking(true);
    setError(null);
    try {
      const active = hasLoadedStatus ? activeShare : await loadActiveShare();
      if (active) {
        setShowReplaceConfirmation(true);
        return;
      }
      await createAndShare();
    } catch {
      // The status/create helpers display the relevant error message.
    } finally {
      setIsWorking(false);
    }
  }, [activeShare, createAndShare, hasLoadedStatus, loadActiveShare]);

  const deactivate = useCallback(async () => {
    if (!supabase || !activeShare) return;
    setIsWorking(true);
    setError(null);
    setMessage(null);
    try {
      const { data, error: deactivateError } = await shareRpc(supabase).rpc("deactivate_share_link", {
        p_share_link_id: activeShare.id,
      });
      if (deactivateError) throw deactivateError;
      if (data !== true) throw new Error("The share link could not be turned off.");
      setActiveShare(null);
      setShareUrl(null);
      setHasLoadedStatus(true);
      setMessage("Sharing is off. The public link can no longer request audio. A signed audio link already issued to an open page may work for up to five minutes.");
    } catch (deactivateError) {
      setError(friendlyErrorMessage(deactivateError, "share", "We couldn’t turn sharing off. Please try again."));
    } finally {
      setIsWorking(false);
    }
  }, [activeShare]);

  async function copyVisibleLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setMessage("Share link copied. Send it to your family.");
    } catch {
      urlInput.current?.focus();
      urlInput.current?.select();
      setMessage("Your link is ready. Select and copy it, then send it to your family.");
    }
  }

  return (
    <div className="share-controls" aria-label="Share this recording">
      {isLoading && <p className="share-status-loading" role="status">Checking sharing…</p>}
      {error && <p className="error-message" role="alert">{error}</p>}
      {message && <p className="setup-message" role="status">{message}</p>}
      {activeShare ? (
        <>
          {!shareUrl && <p className="share-status-on" role="status">Sharing is on</p>}
          {shareUrl ? (
            <>
              <label className="field-label" htmlFor="family-share-link">Family share link</label>
              <input ref={urlInput} className="email-input share-link-input" id="family-share-link" value={shareUrl} readOnly onFocus={(event) => event.currentTarget.select()} />
              <div className="recorder-secondary-actions">
                <button type="button" className="secondary-action recorder-button" onClick={() => void copyVisibleLink()} disabled={isWorking}>Copy link</button>
                <button type="button" className="primary-action recorder-button" onClick={() => void shareOrCopy(shareUrl)} disabled={isWorking}>Share link</button>
              </div>
            </>
          ) : showReplaceConfirmation ? (
            <div className="share-replace-confirmation" role="group" aria-label="Confirm new share link">
              <p>This will stop your current link from working. Anyone you already sent it to will need the new one.</p>
              <div className="recorder-secondary-actions">
                <button type="button" className="primary-action recorder-button" onClick={() => void createAndShare()} disabled={isWorking}>
                  {isWorking ? "Creating your link…" : "Continue"}
                </button>
                <button type="button" className="secondary-action recorder-button" onClick={() => setShowReplaceConfirmation(false)} disabled={isWorking}>Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" className="secondary-action full-width share-action" onClick={() => setShowReplaceConfirmation(true)} disabled={isWorking}>
              Create a new link
            </button>
          )}
          <button type="button" className="secondary-action full-width share-action" onClick={() => void deactivate()} disabled={isWorking}>
            {isWorking ? "Updating sharing…" : "Turn sharing off"}
          </button>
        </>
      ) : (
        <>
          <p className="field-help">Create an unlisted link anyone you send it to can open without signing in. You can turn it off any time.</p>
          <button type="button" className="primary-action full-width share-action" onClick={() => void requestCreateLink()} disabled={isWorking}>
            {isWorking ? "Creating your link…" : "Share with family"}
            {!isWorking && <span aria-hidden="true">→</span>}
          </button>
        </>
      )}
    </div>
  );
}
